import { useRef, useState, type TouchEvent } from "react";
import { ApertureIcon, ArrowClockwiseIcon, CornersOutIcon, FilmStripIcon, PlayIcon, StopIcon, VideoCameraIcon } from "@phosphor-icons/react";
import type { BlinkStatus, Camera } from "../api/types";
import { Button } from "../components/Button";
import { useLiveStream } from "../cameras/useLiveStream";
import { rssiQuality } from "../lib/units";
import { fmtAgoShort } from "../lib/time";
import { useNow } from "../lib/useNow";
import { swipeDirection } from "./cycle";

// The selected camera, floating left of the hologram (mockup #camPanel):
// title line, a 16:9 feed with corner ticks and scan lines, the Live pill,
// one line of meta, and full screen / snapshot / clips. The feed is focusable
// so Tab can switch cameras while it has focus; Escape hands focus on.

export function wifiWord(v: number | null): string | null {
  if (v === null) return null;
  if (v < 0) return rssiQuality(v) === "strong" ? "good" : rssiQuality(v);
  return v >= 4 ? "good" : v >= 2 ? "fair" : "weak";
}

export function CameraPanel({
  cameras,
  camera,
  blink,
  loading,
  error,
  isOwner,
  live,
  setLive,
  snapping,
  onSnapshot,
  onClips,
  onFallbackFull,
  onCycle,
  onConnectBlink,
  onLeaveFeed,
}: {
  cameras: Camera[];
  camera: Camera | null;
  blink: BlinkStatus | undefined;
  loading: boolean;
  error: string | null;
  isOwner: boolean;
  live: boolean;
  setLive: (on: boolean) => void;
  snapping: boolean;
  onSnapshot: () => void;
  onClips: () => void;
  onFallbackFull: () => void;
  onCycle: (dir: 1 | -1) => void;
  onConnectBlink: () => void;
  onLeaveFeed: () => void;
}) {
  const now = useNow();
  const feedRef = useRef<HTMLDivElement>(null);
  const touch = useRef<{ x: number; y: number; t: number } | null>(null);
  const [broken, setBroken] = useState<string | null>(null);
  const stream = useLiveStream(camera?.id ?? null, live && !!camera);
  const { phase } = stream;
  const playing = phase.kind === "playing";
  const connecting = phase.kind === "starting" || phase.kind === "waiting";
  const over = phase.kind === "ended" || phase.kind === "error";

  if (!camera) {
    const s = blink?.status;
    const title = !blink ? "checking blink" : s !== "connected" ? "blink offline" : loading ? "loading" : "no cameras";
    const msg = !blink
      ? "Checking the Blink connection…"
      : s === "needs_2fa"
        ? "Blink is waiting for its verification code."
        : s === "error"
          ? `The Blink connection has a problem.${blink.message ? ` ${blink.message}` : ""}`
          : s !== "connected"
            ? isOwner
              ? "Connect the Blink account to see cameras here."
              : "Blink is not connected yet. Ask an owner to connect it."
            : error
              ? error
              : loading
                ? "Loading cameras…"
                : "No cameras on this Blink account. Add them in the Blink app.";
    return (
      <section className="float left campanel" aria-label="Cameras">
        <div className="ftitle">
          <b>cameras</b>
          <span>{title}</span>
        </div>
        <div className="feed feed--empty" id="cam-feed">
          <i className="tick a" aria-hidden="true" />
          <i className="tick b" aria-hidden="true" />
          <i className="tick c" aria-hidden="true" />
          <i className="tick d" aria-hidden="true" />
          <div className="feed__overlay" role="status">
            <p>{msg}</p>
            {isOwner && blink && s !== "connected" ? (
              <Button variant="primary" size="sm" icon={VideoCameraIcon} onClick={onConnectBlink}>
                {s === "needs_2fa" ? "enter the code" : "connect blink"}
              </Button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  const imgOk = broken !== camera.thumbnail_url;
  const elapsed = Math.max(0, Math.round((now - stream.startedAt) / 1000));
  const right = !camera.online
    ? "offline"
    : playing
      ? `live · ${elapsed}s`
      : connecting
        ? `connecting · ${elapsed}s`
        : camera.thumbnail_at
          ? fmtAgoShort(camera.thumbnail_at, now)
          : "no picture yet";
  const wifi = wifiWord(camera.wifi_strength);
  const meta = [
    camera.temperature_f !== null ? `${Math.round(camera.temperature_f)}°F` : null,
    wifi ? `wifi ${wifi}` : null,
    camera.battery === "low" ? "battery low" : null,
    camera.armed ? "armed" : "disarmed",
  ].filter(Boolean);

  const fullScreen = async () => {
    const el = feedRef.current;
    const video = el?.querySelector("video") as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    try {
      if (el?.requestFullscreen) {
        await el.requestFullscreen();
        return;
      }
      if (video?.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
        return;
      }
    } catch {
      // Fall through to the Cameras section.
    }
    onFallbackFull();
  };

  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    touch.current = t ? { x: t.clientX, y: t.clientY, t: Date.now() } : null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const s = touch.current;
    const t = e.changedTouches[0];
    touch.current = null;
    if (!s || !t) return;
    const dir = swipeDirection(t.clientX - s.x, t.clientY - s.y, Date.now() - s.t);
    if (dir) onCycle(dir);
  };

  const index = cameras.findIndex((c) => c.id === camera.id);
  const many = cameras.length > 1;

  return (
    <section className="float left campanel" aria-label={`Camera: ${camera.name}`}>
      <div className="ftitle">
        <b className="break">{camera.name.toLowerCase()}</b>
        <span className={camera.online ? undefined : "warn-text"}>{right}</span>
      </div>
      <div
        className={`feed${playing ? " feed--live" : ""}`}
        id="cam-feed"
        ref={feedRef}
        tabIndex={0}
        role="group"
        aria-roledescription="camera feed"
        aria-label={`${camera.name}${many ? `, camera ${index + 1} of ${cameras.length}` : ""}`}
        aria-describedby={many ? "cam-hint" : undefined}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onLeaveFeed();
          }
        }}
        onTouchStart={many ? onTouchStart : undefined}
        onTouchEnd={many ? onTouchEnd : undefined}
      >
        {live ? (
          <video
            ref={stream.videoRef}
            className="feed__media"
            poster={imgOk ? camera.thumbnail_url : undefined}
            muted
            autoPlay
            playsInline
            aria-label={`Live video from ${camera.name}`}
          />
        ) : imgOk ? (
          <img className="feed__media" src={camera.thumbnail_url} alt={`Latest picture from ${camera.name}`} onError={() => setBroken(camera.thumbnail_url)} decoding="async" />
        ) : (
          <div className="feed__overlay">
            <p className="muted">no picture yet</p>
          </div>
        )}
        <i className="tick a" aria-hidden="true" />
        <i className="tick b" aria-hidden="true" />
        <i className="tick c" aria-hidden="true" />
        <i className="tick d" aria-hidden="true" />
        <Button
          size="sm"
          variant={playing ? "danger" : "default"}
          icon={live ? StopIcon : PlayIcon}
          className="livepill"
          onClick={() => setLive(!live)}
          disabled={!camera.online && !live}
          aria-label={live ? `Stop live view of ${camera.name}` : `Start live view of ${camera.name}`}
        >
          {playing ? "live" : live ? "…" : "live"}
        </Button>
        {meta.length ? <span className="feedmeta">{meta.join(" · ")}</span> : null}
        {connecting ? (
          <div className="feed__overlay feed__overlay--soft" role="status">
            <p>{phase.kind === "starting" ? "waking the camera" : "waiting for video"} · {elapsed}s</p>
          </div>
        ) : null}
        {over ? (
          <div className="feed__overlay" role="status">
            <p className={phase.kind === "error" ? "red-text" : undefined}>{phase.kind === "error" ? "live view failed" : "live view ended"}</p>
            <p className="muted">{phase.kind === "error" ? phase.message : phase.reason}</p>
            <Button size="sm" variant="primary" icon={ArrowClockwiseIcon} onClick={stream.restart}>
              {phase.kind === "error" ? "try again" : "restart"}
            </Button>
          </div>
        ) : null}
      </div>
      <div className="actions">
        <Button size="sm" icon={CornersOutIcon} onClick={() => void fullScreen()}>
          full screen
        </Button>
        <Button size="sm" icon={ApertureIcon} onClick={onSnapshot} disabled={snapping || !camera.online}>
          {snapping ? "waiting…" : "snapshot"}
        </Button>
        <Button size="sm" icon={FilmStripIcon} onClick={onClips}>
          clips
        </Button>
        {many ? (
          <span className="camhint" id="cam-hint">
            <span className="camhint__key">
              <kbd>tab</kbd> next camera
            </span>
            <span className="camhint__touch">swipe for next camera</span>
            <span className="sr-only">. Shift Tab goes back. Escape leaves the camera feed.</span>
          </span>
        ) : null}
      </div>
    </section>
  );
}
